import { Checkbox, Container, Divider, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import * as _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import type { DateTime } from 'rrule/dist/esm/datetime';

import { AccessLevelType, GeographicRestrictionType } from '@/models/channel';
import { accessUtils } from '@/utils/accessUtils';

import FuelAutoComplete from '../../../components/fuel-autocomplete/FuelAutoComplete';
import FuelTags from '../../../components/fuel-tags';
import { Permission } from '../../../enums';
import { CustomAttributes } from '../../../models/customAttributes';
import type { CustomMetaFieldInfo } from '../../../models/item';
import type { AssetForm } from '../../../store/forms';
import { defaultCustomFields } from '../../../store/forms/Asset-Editor/AssetForm';
import type { SchemaTypeOption } from '../../../store/forms/Asset-Editor/SchemaTypeOptions';
import { SchemaType, SchemaTypeOptions } from '../../../store/forms/Asset-Editor/SchemaTypeOptions';
import { applicationDataSelector } from '../../../store/slices/application-data';
import { fuelUserAccessSliceSelector } from '../../../store/slices/fuelUserAccess';
import { dateUtils } from '../../../utils/dateUtils';
import itemUtils from '../../../utils/itemUtils';
import { validateFileExtension } from '../validateFileExtension';
import AssetVariantList from './AssetVariantList';

interface IProps {
  assetForm: AssetForm;
  assetFormValues: any; //  react hook form
  onFormChanged?: () => void;
  isNew?: boolean;
  rightOpen?: boolean;
  expandRightSide?: (rightOpen) => void;
}

const AssetFieldsWidget = ({
  assetForm,
  assetFormValues,
  isNew = false,
  onFormChanged,
  rightOpen = false,
  expandRightSide,
}: IProps) => {
  const { ouData, categories } = useSelector(applicationDataSelector);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const isSuperAdmin = useMemo(() => permissions && permissions.has(Permission.OUSuperAdmin), [permissions]);

  const { watch } = assetFormValues;
  // const [selectedTags, setSelectedTags] = useState(assetFormValues.getValues('tags'));
  const [isVideo, setIsVideo] = useState<boolean>(assetForm.schemaType === 'Video');
  const [isImage, setIsImage] = useState<boolean>(assetForm.schemaType === 'Image');
  const isNestedChannel = useMemo(() => assetForm.source === 'NestedChannel', [assetForm.source]);
  const [isEditingDisabled, setIsEditingDisabled] = useState(false); // assetForm.permissions && !assetForm.permissions.has(Permission.EditAsset)
  // const fileInput = useRef<HTMLInputElement | null>(null);
  // const [thumbnailFile, setThumbnailFile] = useState(null);
  const [isLongFormContent, setIsLongFormContent] = useState(assetForm.isLongFormContentEnabled);
  const [publicationDate, setPublicationDate] = useState(
    dateUtils.toDate(assetFormValues.getValues('publicationDate'), ouData.timezone)
  ); // publicationDate
  const [embargoDate, setEmbargoDate] = useState(dateUtils.toDate(assetFormValues.getValues('embargoDate'), ouData.timezone)); // embargoDate
  const [expireDate, setExpireDate] = useState(dateUtils.toDate(assetFormValues.getValues('expireDate'), ouData.timezone)); // expireDate
  const isAIAnalyzedApplied = false;
  // const [fuelAIpocFeatureEnable, setFuelAIpocFeatureEnable] = useState(false);
  const tagStateRef = useRef(CustomAttributes.Create());
  const isBatchUploadEnable = watch('isBatchUploadEnable');
  const publicationDateWatch = watch('publicationDate');
  const embargoDateWatch = watch('embargoDate');
  const expireDateWatch = watch('expireDate');
  const schemaType = watch('schemaType');
  const sourceWatch = watch('source');

  useEffect(() => {
    // init code here
    // console.log('AssetFieldsWidget init', assetForm);
    // setFuelAIpocFeatureEnable(flags['fuel-ai-poc-features']);
    setTimeout(() => {
      const newAssetForm = assetFormValues.getValues();
      // console.log('AssetFieldsWidget init', { newAssetForm });
      if (newAssetForm) {
        setPublicationDate(dateUtils.toDate(newAssetForm.publicationDate, ouData.timezone));
        setEmbargoDate(dateUtils.toDate(newAssetForm.embargoDate, ouData.timezone));
        setExpireDate(dateUtils.toDate(newAssetForm.expireDate, ouData.timezone));
        setIsEditingDisabled(!permissions.has(Permission.EditAsset));
      }
    }, 300);
    return () => {};
  }, []);

  useEffect(() => {
    setIsLongFormContent(rightOpen);
  }, [rightOpen]);

  useEffect(() => {
    if (publicationDateWatch) {
      setPublicationDate(dateUtils.toDate(publicationDateWatch, ouData.timezone));
    }
  }, [publicationDateWatch]);
  useEffect(() => {
    setEmbargoDate(dateUtils.toDate(embargoDateWatch, ouData.timezone));
  }, [embargoDateWatch]);
  useEffect(() => {
    setExpireDate(dateUtils.toDate(expireDateWatch, ouData.timezone));
  }, [expireDateWatch]);
  useEffect(() => {
    if (schemaType === 'Video') {
      setIsVideo(schemaType === 'Video');
    } else if (schemaType === 'Image') {
      setIsImage(schemaType === 'Image');
    }
  }, [schemaType]);

  // const populateLongFormFields = (assetForm) => {
  //   // console.log('populateLongFormFields', assetForm);
  //   refreshLongFormFields(assetForm.tags);

  // };

  const handleMediaTypeChange = (value: SchemaTypeOption) => {
    // console.log('selected media type', value);
    if (value.id !== SchemaType.Video) {
      assetFormValues.setValue('isLongFormContentEnabled', false);
    }
    assetFormValues.setValue('schemaType', value.id, { shouldValidate: true });
    setIsVideo(value && value.id === SchemaType.Video);
    setIsImage(value && value.id === SchemaType.Image);
    assetFormValues.setValue('title', '');
    const ingestFiles = assetFormValues.getValues('ingestFiles');
    const files: File[] = [];
    for (let i = 0; i < ingestFiles.length; i++) {
      const error = validateFileExtension(value.id, ingestFiles[i].name);
      if (!error) {
        files.push(ingestFiles[i]);
      }
    }
    assetFormValues.setValue('isBatchUploadEnable', files.length > 1, { shouldValidate: true });
    if (onFormChanged) {
      onFormChanged();
    }
  };

  const showAutomatedTransition = (): Boolean => {
    const asset = assetForm.originalAssetData;
    if (isNestedChannel) {
      return false;
    }
    if (asset && asset.data && asset.data.clipHLSData && asset.data.clipHLSData.externalHLS) {
      return false;
    }
    return true;
  };
  const onChangeDate = (value) => {
    // console.log('onChangeDate', { value });
    setPublicationDate(value);
    assetFormValues.setValue('publicationDate', dateUtils.toString(value, ouData.timezone));
  };

  const onTitleBlur = (evt) => {
    const value = evt && evt.target ? evt.target.value : null;
    // console.log('onTitleBlur', { value, evt });
    if (value) {
      const trimmedVal = value.trim();
      evt.target.value = trimmedVal;
      assetFormValues.setValue('title', trimmedVal);
      if (onFormChanged) {
        onFormChanged();
      }
    }
  };

  // Long Form Content regions
  const onLongFormToggleClicked = (event) => {
    // console.log('onLongFormToggleClicked', { event });
    const { checked } = event.target;
    const existingFields: CustomMetaFieldInfo[] = assetFormValues.getValues('customFields');
    const defaultFields = _.cloneDeep(defaultCustomFields);
    if (checked) {
      for (let i = 0; i < defaultFields.length; i++) {
        const element = defaultFields[i];
        const found = _.find(existingFields, (f) => f.key === element.key);
        if (!found) {
          existingFields.push(element);
        }
      }
    }
    assetFormValues.setValue('customFields', existingFields);
    assetFormValues.setValue('isLongFormContentEnabled', checked);
    // assetForm.isLongFormContentEnabled = checked;
    setIsLongFormContent(checked);
    if (expandRightSide) expandRightSide(!rightOpen);
  };

  const cleanUpMetaTags = (value) => {
    const existingTags: string[] = value;
    const tagsDict = {};
    const newTags: string[] = [];
    for (let i = 0; i < existingTags.length; i++) {
      const t = existingTags[i];
      if (itemUtils.isMetaTag(t)) {
        const { key, value } = itemUtils.getMetaTagKeyValue(t);
        if (key && itemUtils.isSingleValueTagField(key)) {
          if (!tagsDict.hasOwnProperty(key)) {
            newTags.push(t);
            tagsDict[key] = value;
          }
        } else {
          if (key === 'actor' || key === 'cast') {
            if (newTags.indexOf(`actor:${value}`) === -1 && newTags.indexOf(`cast:${value}`) === -1) {
              newTags.push(t);
            }
          } else {
            newTags.push(t);
          }
          tagsDict[key] = value;
        }
      } else {
        newTags.push(t);
      }
    }
    return newTags;
  };
  const onTagsChanged = (value) => {
    const newTags: string[] = cleanUpMetaTags(value);
    assetFormValues.setValue('tags', newTags, { shouldValidate: true });
    refreshLongFormFields(newTags);
  };

  // called when tags change and we need to update the special fields
  const refreshLongFormFields = (tags) => {
    const myState = _.cloneDeep(tagStateRef.current);
    // console.log('refreshLongFormFields', tags);
    for (const fieldKey in myState) {
      // console.log('refreshLongFormFields fieldKey', fieldKey);
      const myvalues = [];
      if (tags && tags.length > 0) {
        for (let i = 0; i < tags.length; i++) {
          const tag = tags[i];
          if (itemUtils.isMetaTag(tag)) {
            const { key, value } = itemUtils.getMetaTagKeyValue(tag);
            if (key === fieldKey || (fieldKey === 'actor' && key === 'cast')) {
              myvalues.push(value);
            }
          }
        }
      }

      let fieldValue = _.join(myvalues, ', ');
      // just take the first tag for these special one liner metatags
      if (myvalues.length > 1 && itemUtils.isSingleValueTagField(fieldKey)) {
        fieldValue = myvalues[0];
      }
      myState[fieldKey] = fieldValue;
    }

    tagStateRef.current = myState;
    if (onFormChanged) {
      onFormChanged();
    }
  };

  const getInputLableProps = (value: string) => {
    if (value) {
      return { shrink: true };
    }
    return {};
  };

  // useEffect(() => {
  //   // console.log(isEditingDisabled,  isNew, isClipGroupSource)
  // }, []);

  const onExpireDateChanged = (value: DateTime) => {
    // console.log('onExpireDateChanged', { value });
    const oldExpire = assetFormValues.getValues('expireDate');
    const newExpire = dateUtils.toString(value, ouData.timezone);
    let accessSettings = _.cloneDeep(assetFormValues.getValues('accessSettings'));

    if (accessSettings == null) {
      accessSettings = {
        accessLevel: ouData?.metaData?.defaultAccessLevel || AccessLevelType.Free,
        effectiveAccessLevel: ouData?.metaData?.defaultAccessLevel || AccessLevelType.Free,
        geographic: {
          geoblockingType: GeographicRestrictionType.NoGeoblocking,
          countries: [],
        },
        demographic: {
          ageGate: null,
        },
        temporal: {
          accessSchedules: [],
        },
      };
    }
    if (accessSettings.temporal == null) {
      accessSettings.temporal = {};
    }
    if (accessSettings.temporal.accessSchedules == null) {
      accessSettings.temporal.accessSchedules = [];
    }

    // Use the util function to update accessSchedules accordingly
    accessSettings.temporal.accessSchedules = accessUtils.ensureExpireSet(
      accessSettings.temporal.accessSchedules,
      oldExpire,
      newExpire
    );

    setExpireDate(value);
    assetFormValues.setValue('expireDate', newExpire);
    assetFormValues.setValue('accessSettings', accessSettings);
  };

  const onEmbargoDateChanged = (value: DateTime) => {
    // console.log('onEmbargoDateChanged', { value });
    const oldEmbargo = assetFormValues.getValues('embargoDate');
    const newEmbargo = dateUtils.toString(value, ouData.timezone);
    let accessSettings = _.cloneDeep(assetFormValues.getValues('accessSettings'));

    if (accessSettings == null) {
      accessSettings = {
        accessLevel: ouData?.metaData?.defaultAccessLevel || AccessLevelType.Free,
        effectiveAccessLevel: ouData?.metaData?.defaultAccessLevel || AccessLevelType.Free,
        geographic: {
          geoblockingType: GeographicRestrictionType.NoGeoblocking,
          countries: [],
        },
        demographic: {
          ageGate: null,
        },
        temporal: {
          accessSchedules: [],
        },
      };
    }
    if (accessSettings.temporal == null) {
      accessSettings.temporal = {};
    }
    if (accessSettings.temporal.accessSchedules == null) {
      accessSettings.temporal.accessSchedules = [];
    }

    // Use the util function to update accessSchedules accordingly
    accessSettings.temporal.accessSchedules = accessUtils.ensureEmbargoSet(
      accessSettings.temporal.accessSchedules,
      oldEmbargo,
      newEmbargo
    );

    setEmbargoDate(value);
    assetFormValues.setValue('embargoDate', newEmbargo);
    assetFormValues.setValue('accessSettings', accessSettings);
  };

  return (
    <div id="asset-fields-widget" test-id="asset-fields-widget">
      <Container component="section">
        <Grid container className="section-box" spacing={2}>
          <Grid item xs={6}>
            <FuelAutoComplete
              id="media-type-select"
              items={SchemaTypeOptions}
              itemText="schemaName"
              itemKey="id"
              labelText="Media Type"
              multiSelect={false}
              placeHolderText="Select Media Type"
              labelPaddingTop="-5px"
              disabled={isEditingDisabled || !isNew || sourceWatch === 'ClipGroup'}
              defaultValue={SchemaTypeOptions.find((s) => s.id === assetForm.schemaType) ?? ''}
              onChange={handleMediaTypeChange}
            />
          </Grid>
          <Grid item xs={6}>
            {(isVideo || isImage) && (
              <FuelAutoComplete
                id="category-select"
                items={categories}
                itemText="name"
                itemKey="id"
                labelText="Categories"
                placeHolderText="Select Categories"
                multiSelect
                labelPaddingTop="-5px"
                defaultValue={assetFormValues.getValues('categories') ?? []}
                onChange={(value) => assetFormValues.setValue('categories', value, { shouldValidate: true })}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            <TextField
              variant="outlined"
              label={isAIAnalyzedApplied ? 'Title (Suggested Ttitle)' : 'Title'}
              fullWidth
              size="small"
              disabled={isNestedChannel || isEditingDisabled || isBatchUploadEnable}
              placeholder="Enter title"
              {...assetFormValues.register('title')}
              error={assetForm.errors && assetForm.errors.has && assetForm.errors.has('title')}
              // required
              inputProps={{
                maxLength: 250,
              }}
              onBlur={onTitleBlur}
              InputLabelProps={getInputLableProps(assetFormValues.watch('title'))}
            />
            {assetForm.errors && assetForm.errors.has && assetForm.errors.has('title') && (
              <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                {assetForm.errors.message('title')}
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            {(isVideo || isImage) && (
              <div>
                <FuelTags
                  id="fuel-ingest-tag-id"
                  labelText={isAIAnalyzedApplied ? 'Tags (Suggested Tags)' : 'Tags'}
                  tags={assetFormValues.getValues('tags')}
                  onChange={onTagsChanged}
                />
              </div>
            )}
          </Grid>
          {schemaType === 'Video' && (
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                label="Sub-title"
                fullWidth
                size="small"
                disabled={isNestedChannel || isEditingDisabled || isBatchUploadEnable}
                placeholder="Enter Sub-title"
                {...assetFormValues.register('subTitle')}
                inputProps={{
                  maxLength: 250,
                }}
                InputLabelProps={getInputLableProps(assetFormValues.watch('subTitle'))}
              />
            </Grid>
          )}
          <Grid
            className="include-automated-transitons-container"
            xs={6}
            item
            sx={{
              display: { xs: schemaType === 'Video' ? 'flex' : 'none' },
            }}
          >
            {showAutomatedTransition() && isVideo && (
              <FormControlLabel
                control={
                  <Controller
                    name="applyAutomatedTransitions"
                    control={assetFormValues.control}
                    render={({ field: props }) => (
                      <Checkbox
                        {...props}
                        checked={props.value}
                        color="success"
                        disabled={isEditingDisabled || sourceWatch === 'ClipGroup'}
                        style={{
                          transform: 'scale(.6)',
                        }}
                        onChange={(e) => props.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                // label="Include Automated Transitions"
                label={
                  <Typography title="Include Automated Transitions" sx={{ marginTop: '-6px' }}>
                    Include Automated Transitions
                  </Typography>
                }
              />
            )}
            {isVideo && (
              <FormControlLabel
                sx={{ cursor: assetForm.mode === 'new' ? 'not-allowed' : 'cursor' }}
                control={
                  <Controller
                    name="doNotDelete"
                    control={assetFormValues.control}
                    render={({ field: props }) => (
                      <Checkbox
                        {...props}
                        checked={props.value}
                        color="success"
                        disabled={assetForm.mode === 'new'}
                        style={{
                          transform: 'scale(.6)',
                        }}
                        onChange={(e) => props.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                // label="Include Automated Transitions"
                label={
                  <Typography title="Retain/Do Not Delete" sx={{ marginTop: '-6px' }}>
                    Retain/Do Not Delete
                  </Typography>
                }
              />
            )}
          </Grid>
          {(schemaType === 'Video' || schemaType === 'Image') && (
            <Grid
              item
              xs={6}
              // sx={{
              //   display: { xs: isVideo || isImage ? '' : 'none' },
              // }}
            >
              <Controller
                control={assetFormValues.control}
                name="publicationDate"
                // rules={{ required: true }} //optional
                render={({
                  field: { onChange, name, value },
                  fieldState: { invalid, isDirty }, // optional
                  formState: { errors }, // optional, but necessary if you want to show an error message
                }) => (
                  <>
                    <DateTimePicker
                      inputFormat="MM/dd/yyyy hh:mm a"
                      label={`Publication Date (${ouData.timezoneAbbreviation})`}
                      value={publicationDate}
                      onChange={(value) => {
                        setPublicationDate(dateUtils.toString(value, ouData.timezone));
                        assetFormValues.setValue('publicationDate', dateUtils.toString(value, ouData.timezone));
                      }}
                      InputProps={{
                        title: 'Choose Date/Time',
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          InputProps={{
                            title: 'Choose Date/Time',
                          }}
                          {...params}
                        />
                      )}
                    />
                    {/* <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                    {errors && errors['publicationDate'] && errors['publicationDate'].type === 'required' && (
                      //if you want to show an error message
                      <span>Publication Date is required.</span>
                    )}
                  </Typography> */}
                    {assetForm.errors && assetForm.errors.has && assetForm.errors.has('publicationDate') && (
                      <Typography variant="inherit" className="text-danger" test-id="publication-date">
                        {assetForm.errors.message('publicationDate')}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Grid>
          )}

          {schemaType === 'Video' && (
            <Grid item xs={3}>
              <Controller
                control={assetFormValues.control}
                name="embargoDate"
                render={({
                  field: { onChange, name, value },
                  fieldState: { invalid, isDirty }, // optional
                  formState: { errors }, // optional, but necessary if you want to show an error message
                }) => (
                  <>
                    {!(sourceWatch === 'ClipGroup') && (
                      <DateTimePicker
                        inputFormat="MM/dd/yyyy hh:mm a"
                        label={`Embargo (${ouData.timezoneAbbreviation})`}
                        value={embargoDate}
                        onChange={onEmbargoDateChanged}
                        InputProps={{
                          title: 'Choose Date/Time',
                          className: 'embargo-expire-date-input',
                        }}
                        renderInput={(params) => (
                          <TextField
                            size="small"
                            key="embargo-date"
                            variant="outlined"
                            InputProps={{
                              title: 'Choose Date/Time',
                              className: 'embargo-expire-date-input',
                            }}
                            {...params}
                          />
                        )}
                      />
                    )}
                    <Typography variant="inherit" className="text-danger embargo-expire-error-section" test-id="ingest-title">
                      {assetForm.errors.has('embargoDate') && !(sourceWatch === 'ClipGroup') && (
                        // if you want to show an error message
                        <span>{assetForm.errors.message('embargoDate')}</span>
                      )}
                    </Typography>
                  </>
                )}
              />
            </Grid>
          )}
          {schemaType === 'Video' && !(sourceWatch === 'ClipGroup') && (
            <Grid item xs={3}>
              <Controller
                control={assetFormValues.control}
                name="expireDate"
                render={({
                  field: { onChange, name, value },
                  fieldState: { invalid, isDirty }, // optional
                  formState: { errors }, // optional, but necessary if you want to show an error message
                }) => (
                  <>
                    <DateTimePicker
                      inputFormat="MM/dd/yyyy hh:mm a"
                      label={`Expires (${ouData.timezoneAbbreviation})`}
                      value={expireDate}
                      onChange={onExpireDateChanged}
                      InputProps={{
                        title: 'Choose Date/Time',
                        className: 'embargo-expire-date-input',
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          key="expire-date"
                          variant="outlined"
                          InputProps={{
                            title: 'Choose Date/Time',
                            className: 'embargo-expire-date-input',
                          }}
                          {...params}
                        />
                      )}
                    />
                    <Typography variant="inherit" className="text-danger embargo-expire-error-section" test-id="ingest-title">
                      {assetForm.errors.has('expireDate') && (
                        // if you want to show an error message
                        <span>{assetForm.errors.message('expireDate')}</span>
                      )}
                    </Typography>
                  </>
                )}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField
              className="asset-synopsis"
              variant="outlined"
              label={isAIAnalyzedApplied ? 'Description (Suggested Description)' : 'Description'}
              fullWidth
              placeholder="Describe your clip..."
              multiline
              maxRows={8}
              minRows={2}
              disabled={isEditingDisabled || isBatchUploadEnable}
              {...assetFormValues.register('synopsis')}
              onChange={(e) => getInputLableProps(e.target.value)}
              InputLabelProps={getInputLableProps(assetForm.synopsis)}
            />
          </Grid>
          {schemaType === 'Video' && (
            <Grid item xs={6}>
              <div className="is-long-form-content-toggle-container">
                <FormControlLabel
                  value="start"
                  control={
                    <Switch
                      className="fuel-toggle"
                      sx={{ marginTop: `0px`, marginLeft: `-3px` }}
                      inputProps={{ 'aria-label': 'Is Movie / Series Episode?' }}
                      checked={isLongFormContent}
                      onChange={onLongFormToggleClicked}
                      size="small"
                    />
                  }
                  label="Movie / Series Episode"
                  labelPlacement="start"
                />
              </div>
            </Grid>
          )}
          {!isNew && schemaType === 'Video' && (
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                label="Midroll Ad Cue Points"
                fullWidth
                size="small"
                disabled
                placeholder=""
                value={assetFormValues.getValues('markers')?.join(', ')}
                inputProps={{
                  maxLength: 250,
                }}
              />
            </Grid>
          )}

          {isSuperAdmin && !isNew && schemaType === 'Video' && sourceWatch != 'NestedChannel' && sourceWatch != 'ClipGroup' && (
            <Grid item xs={12}>
              <Divider />
              <Typography component="div" sx={{ marginTop: 2, marginBottom: 1 }}>
                Variants
              </Typography>
              <AssetVariantList assetFormValues={assetFormValues} />
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default AssetFieldsWidget;
